import {useUpmPlantsHierarchy} from '@domain/upm'
import {FlexPage} from '@hconnect/common/components/FlexPage'
import {useWidth} from '@hconnect/common/hooks/useWidth'
import {
  FilterContrastProvider,
  getButtonOutlineWhite,
  useInvertedSurface
} from '@hconnect/common/styles'
import {getSystemTimezone} from '@hconnect/common/utils'
import {IconWithLabelButton} from '@hconnect/uikit'
import {EXTENDED_QUICK_SELECTION} from '@hconnect/uikit/src/lib2/components/simpleDatePicker/dateRangePickerHelpers'
import {useUpmPlantsContext} from '@hconnect/upm'
import FileDownloadIcon from '@mui/icons-material/FileDownload'
import {Box, Button, Grid, Tooltip, Typography} from '@mui/material'
import {useCallback, useMemo, useState} from 'react'
import {useTranslation} from 'react-i18next'

import {FEATURE_NAMESPACE, TasksFilterKey} from '../consts'
import {TasksContainer, TasksFilters} from '../containers'
import {
  useExportToExcel,
  useExportCostLeadership,
  useTasksPageFilters,
  useTasksTableFilters,
  useTasks,
  useTasksInfiniteQuery
} from '../hooks'
import {mapTasksFiltersParams} from '../mappers'
import {TasksFiltersState} from '../types'
import {resolveAreaPlantIds} from '../utils'

const EXPORT_LIMIT = 5000
const DEFAULT_PAGE_SIZE = 50

export const Tasks: React.FC = () => {
  const {t} = useTranslation(FEATURE_NAMESPACE)
  const breakPoint = useWidth()
  const isSmall = breakPoint === 'xs' || breakPoint === 'sm'
  const timezone = getSystemTimezone()
  const [pageNumber, changePage] = useState<number>(0)
  const inverted = useInvertedSurface()

  const {data: upmPlantsHierarchyData} = useUpmPlantsHierarchy()
  const upmPlants = useUpmPlantsContext()

  const {filters, sorting, updateFilters} = useTasksPageFilters({
    onFiltersChanged: () => changePage(0)
  })

  // Created Date defaults to last year until the user removes it in this session
  const [isCreatedDateDefaultApplied, setIsCreatedDateDefaultApplied] = useState(true)
  const resolvedFilters = useMemo(() => {
    if (!isCreatedDateDefaultApplied || filters[TasksFilterKey.CREATED_DATE_RANGE] !== undefined) {
      return filters
    }
    return {
      [TasksFilterKey.CREATED_DATE_RANGE]: EXTENDED_QUICK_SELECTION.LAST_YEAR,
      ...filters
    }
  }, [filters, isCreatedDateDefaultApplied])

  const handleFiltersChange = useCallback(
    (newFilters: TasksFiltersState) => {
      setIsCreatedDateDefaultApplied(newFilters[TasksFilterKey.CREATED_DATE_RANGE] !== undefined)
      updateFilters(newFilters)
    },
    [updateFilters]
  )

  const areaPlantIds = useMemo(
    () =>
      resolveAreaPlantIds({
        areaHierarchyId: resolvedFilters.area,
        hierarchy: upmPlantsHierarchyData?.hierarchy,
        upmPlants
      }),
    [resolvedFilters.area, upmPlantsHierarchyData, upmPlants]
  )

  const tasksTableFiltersOptions = useTasksTableFilters({
    onFiltersChanged: () => changePage(0)
  })
  const {mutate: exportToExcel, isPending: isExporting} = useExportToExcel()
  const {mutate: exportCostLeadership, isPending: isExportingCostLeadership} =
    useExportCostLeadership()

  const [itemsPerPage, setItemsPerPage] = useState<number>(DEFAULT_PAGE_SIZE)
  const tasksQueryParams = {
    limit: itemsPerPage,
    pageNumber,
    assignedToMe: tasksTableFiltersOptions.assignedToMe,
    createdByMe: tasksTableFiltersOptions.createdByMe,
    sources: tasksTableFiltersOptions.sourceFilters,
    filters: resolvedFilters,
    sorting,
    timezone,
    areaPlantIds
  }
  const {data: desktopData, isLoading: isDesktopDataLoading} = useTasks(tasksQueryParams, {
    enabled: !isSmall
  })
  const {
    data: mobileTasksData,
    isLoading: isMobileDataLoading,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage
  } = useTasksInfiniteQuery(tasksQueryParams, {
    enabled: isSmall
  })
  const totalItemCount = isSmall
    ? (mobileTasksData?.totalItemCount ?? 0)
    : (desktopData?.totalItemCount ?? 0)
  const isLoading = isSmall ? isMobileDataLoading : isDesktopDataLoading
  const isExportDisabled = isExporting || isLoading || totalItemCount > EXPORT_LIMIT

  return (
    <FlexPage
      useViewportHeight={!isSmall}
      title={t('pageTitle')}
      inverted={inverted}
      headerActionContent={
        <FilterContrastProvider isContrastMode={inverted}>
          <Grid spacing={1} container alignItems="center" justifyContent="flex-end" wrap="nowrap">
            <Grid item>
              <TasksFilters
                filters={resolvedFilters}
                onFiltersChange={handleFiltersChange}
                timezone={timezone}
                hierarchyData={upmPlantsHierarchyData?.hierarchy}
              />
            </Grid>
            <Grid item>
              <Box sx={{display: 'flex', gap: 1, alignItems: 'stretch'}}>
                <Box sx={{display: 'flex'}}>
                  <Button
                    disabled={isExportingCostLeadership || isLoading}
                    onClick={() =>
                      exportCostLeadership(
                        mapTasksFiltersParams(timezone, resolvedFilters, areaPlantIds)
                      )
                    }
                    sx={getButtonOutlineWhite}
                    data-test-id="tasks-export-cost-leadership"
                    startIcon={<FileDownloadIcon />}
                  >
                    <Box
                      component="span"
                      sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'flex-start',
                        lineHeight: 1.1
                      }}
                    >
                      <Typography variant="body2" component="span" color="inherit">
                        {t('pomAction.action.exportCostLeadershipLine1')}
                      </Typography>
                      <Typography variant="caption" component="span" color="inherit">
                        {t('pomAction.action.exportCostLeadershipLine2')}
                      </Typography>
                    </Box>
                  </Button>
                </Box>
                <Tooltip
                  placement="top"
                  title={
                    !isLoading &&
                    totalItemCount > EXPORT_LIMIT &&
                    t('shiftEvent.label.exportLimitExceeded', {
                      limit: EXPORT_LIMIT
                    })
                  }
                >
                  <Box sx={{display: 'flex'}}>
                    <IconWithLabelButton
                      disabled={isExportDisabled}
                      onClick={() =>
                        exportToExcel({
                          sources: tasksTableFiltersOptions.sourceFilters,
                          filters: mapTasksFiltersParams(timezone, resolvedFilters, areaPlantIds),
                          ...sorting
                        })
                      }
                      sx={[getButtonOutlineWhite, {height: '100%'}]}
                      data-test-id="tasks-export-to-excel"
                      startIcon={<FileDownloadIcon />}
                      label={t('shiftEvent.action.export')}
                    />
                  </Box>
                </Tooltip>
              </Box>
            </Grid>
          </Grid>
        </FilterContrastProvider>
      }
    >
      <TasksContainer
        timezone={timezone}
        tasksTableFiltersOptions={tasksTableFiltersOptions}
        paginationOptions={{pageNumber, changePage}}
        isLoading={isLoading}
        desktopData={desktopData}
        mobileTasksData={mobileTasksData?.pages.flatMap((page) => page)}
        hasNextPage={hasNextPage}
        isFetchingNextPage={isFetchingNextPage}
        fetchNextPage={() => void fetchNextPage()}
        itemsPerPage={itemsPerPage}
        setItemsPerPage={setItemsPerPage}
        tasksQueryParams={tasksQueryParams}
      />
    </FlexPage>
  )
}
